import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation, useQuery } from "convex/react";
import { SearchIcon, UserCogIcon, XCircleIcon } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";

/**
 * Staff impersonation controls; reloads page after start/stop to refresh
 * JWT-bound queries (mirrors OrgSwitcher reload pattern).
 */

function useReloadAfter<T>(fn: (arg: T) => Promise<unknown>) {
	const navigate = useNavigate();
	return useReactQueryMutation({
		mutationFn: async (arg: T) => {
			await fn(arg);
			navigate(0);
		},
	});
}

export function ImpersonationSidebarControl() {
	const isStaff = useQuery(api.staff.isCurrentUserStaff);
	const active = useQuery(api.staffImpersonation.getActiveImpersonation);
	const stopImpersonation = useMutation(
		api.staffImpersonation.stopImpersonation,
	);

	const stop = useReloadAfter(async () => {
		await stopImpersonation();
	});

	if (!isStaff) return null;

	if (active) {
		return (
			<Button
				variant="outline"
				size="sm"
				className="w-full justify-start gap-2 border-accent bg-accent text-accent-foreground hover:bg-accent/80 group-data-[collapsible=icon]:hidden"
				onClick={() => stop.mutate(undefined as unknown as undefined)}
				disabled={stop.isPending}
			>
				<XCircleIcon className="size-4 shrink-0" />
				<span className="truncate">
					{stop.isPending ? "Stopping..." : "Stop impersonating"}
				</span>
			</Button>
		);
	}

	return <ImpersonationPickerButton />;
}

export function impersonationSearchArgs(
	query: string,
): "skip" | { query: string } {
	const normalized = query.trim();
	return normalized.length < 2 ? "skip" : { query: normalized };
}

function ImpersonationPickerButton() {
	const [open, setOpen] = useState(false);
	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger
				render={
					<Button
						variant="ghost"
						size="sm"
						className="w-full justify-start gap-2 group-data-[collapsible=icon]:hidden"
					/>
				}
			>
				<UserCogIcon className="size-4 shrink-0" />
				<span className="truncate">Impersonate user</span>
			</DialogTrigger>
			{open ? (
				<ImpersonationPickerDialogContent onClose={() => setOpen(false)} />
			) : null}
		</Dialog>
	);
}

function ImpersonationPickerDialogContent({
	onClose,
}: {
	onClose: () => void;
}) {
	const [query, setQuery] = useState("");
	const [reason, setReason] = useState("");
	const searchArgs = impersonationSearchArgs(query);
	const results = useQuery(
		api.staffImpersonation.searchUsersForImpersonation,
		searchArgs,
	);
	const startImpersonation = useMutation(
		api.staffImpersonation.startImpersonation,
	);

	const start = useReloadAfter(async (arg: { userId: Id<"users"> }) => {
		await startImpersonation({
			targetUserId: arg.userId,
			reason: reason.trim() || undefined,
		});
		onClose();
	});

	const errMsg = start.error instanceof Error ? start.error.message : null;

	return (
		<DialogContent className="max-w-md">
			<DialogHeader>
				<DialogTitle>Impersonate a user</DialogTitle>
				<DialogDescription>
					You will act as the selected user across the web app, Outlook add-in,
					and Teams bot until you stop. Only non-staff users can be
					impersonated.
				</DialogDescription>
			</DialogHeader>
			<div className="flex flex-col gap-3">
				<div className="relative">
					<SearchIcon className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
					<Input
						autoFocus
						placeholder="Search by name or email"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						className="pl-8"
					/>
				</div>
				<Input
					placeholder="Reason (optional, audit log)"
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					maxLength={200}
				/>
				{errMsg && <p className="text-xs text-destructive">{errMsg}</p>}
				<ScrollArea className="max-h-72 rounded-md border">
					{searchArgs === "skip" ? (
						<p className="p-3 text-sm text-muted-foreground">
							Type at least 2 characters to search
						</p>
					) : results === undefined ? (
						<p className="p-3 text-sm text-muted-foreground">Loading...</p>
					) : results.length === 0 ? (
						<p className="p-3 text-sm text-muted-foreground">No users match</p>
					) : (
						<ul className="divide-y">
							{results.map((u) => (
								<li key={u.userId}>
									<button
										type="button"
										className="flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-accent disabled:opacity-50"
										onClick={() => start.mutate({ userId: u.userId })}
										disabled={start.isPending}
									>
										<span className="text-sm font-medium">{u.name}</span>
										<span className="text-xs text-muted-foreground">
											{u.email}
											{u.defaultTenantName ? ` · ${u.defaultTenantName}` : ""}
										</span>
									</button>
								</li>
							))}
						</ul>
					)}
				</ScrollArea>
			</div>
		</DialogContent>
	);
}

export function ImpersonationBanner() {
	const active = useQuery(api.staffImpersonation.getActiveImpersonation);
	const stopImpersonation = useMutation(
		api.staffImpersonation.stopImpersonation,
	);
	const stop = useReloadAfter(async () => {
		await stopImpersonation();
	});

	if (!active) return null;

	return (
		<div className="z-30 flex items-center justify-between gap-3 border-b border-accent bg-accent px-4 py-2 text-sm text-accent-foreground">
			<div className="flex min-w-0 items-center gap-2">
				<UserCogIcon className="size-4 shrink-0" />
				<span className="truncate">
					Acting as <strong>{active.target.name}</strong>{" "}
					<span className="text-accent-foreground/70">
						({active.target.email}) at {active.tenant.name}
					</span>
					{active.reason ? (
						<span className="text-accent-foreground/70">
							{" "}
							— {active.reason}
						</span>
					) : null}
				</span>
			</div>
			<Button
				variant="ghost"
				size="sm"
				onClick={() => stop.mutate(undefined as unknown as undefined)}
				disabled={stop.isPending}
				className="shrink-0 text-accent-foreground hover:bg-accent/80"
			>
				{stop.isPending ? "Stopping..." : "Stop impersonating"}
			</Button>
		</div>
	);
}
